'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { getTestLabels } from '../../../../utils/getTestLabels.js';
import style from './RangeSlider.module.scss.js';

const { componentTestLabel } = getTestLabels('RangeSlider');
/**
 * Try parsing `value` as a number, falling back to `fallback` if it fails.
 */
const tryParse = (value, fallback) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
};
/**
 * Wrap `callback` in a new function that no matter how often you call it, will
 * only execute `callback` after `limit` milliseconds have passed since the last
 * call.
 */
function useDebounce(callback, limit) {
    const timer = useRef(null);
    const debouncedFunction = useCallback((...args) => {
        // Clear any previous timer to restart the debounce timer
        if (timer.current)
            clearTimeout(timer.current);
        // Set a new timer that will execute the callback after the limit time
        timer.current = setTimeout(() => {
            callback(...args);
            timer.current = null;
        }, limit);
    }, [callback, limit]);
    return debouncedFunction;
}
function roundRanges({ low, high }) {
    return {
        low: Math.floor(low),
        high: Math.ceil(high),
    };
}
/**
 * **[source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Slider/RangeSlider.tsx)**
 *
 * @param {object} boundaryValues - (required) Setting the low and high boundary values of the input. `low < high` for it to work.
 * @param {object} initialValues - (optional) Setting the initial value of the higher and lower input. `low < high` for it to work.
 * @param {string} label - (optional) Setting the visual label of the RangeSlider.
 * @param {number} minGap - (optional) Setting the minimum gap between the low and high value. The default is 1.
 * @param {object} value - (optional) Setting the high and low value when component is controlled.
 * @param {function} onValueChange - (required) A custom function to call when the input value is changed. Receives the current values as `{low: number, high: number}`.
 * @param {number} outputDebounceTime - (optional) Sets the delay (in milliseconds) between when the user finishes interacting and when `onValueChange` is triggered. Defaults to 400ms.
 * @param {string} rangeInputHighAriaLabel - (required) An accessible label (in the current language) for the higher slider input (Use a word or phrase like "Highest price" or "Highest price threshold" in the current language).
 * @param {string} rangeInputLowAriaLabel - (required) An accessible label (in the current language) for the lower slider input (Use a word or phrase like "Lowest price" or "Lowest price threshold" in the current language).
 * @param {string} textInputHighAriaLabel - (optional) An accessible label (in the current language) for the higher text input (Use a word or phrase like "Highest price" or "Highest price threshold" in the current language). Defaults to `rangeInputHighAriaLabel` if not set.
 * @param {string} textInputLowAriaLabel - (optional) An accessible label (in the current language) for the lower text input (Use a word or phrase like "Lowest price" or "Lowest price threshold" in the current language). Defaults to `rangeInputLowAriaLabel` if not set.
 */
const RangeSlider = forwardRef((props, ref) => {
    const { className, label, boundaryValues, minGap = 1, value: controlledValue, onValueChange: unDebouncedOnValueChange, outputDebounceTime = 400, rangeInputHighAriaLabel, rangeInputLowAriaLabel, textInputHighAriaLabel, textInputLowAriaLabel, initialValues = roundRanges(boundaryValues), ...otherProps } = props;
    const roundedBoundaryValues = useMemo(() => roundRanges(boundaryValues), [boundaryValues]);
    // stand-in in case the user doesn't pass `value` and wants to use RangeSlider
    // in an uncontrolled way
    const [uncontrolledValue, setUncontrolledValue] = useState(initialValues);
    const value = useMemo(() => roundRanges(controlledValue || uncontrolledValue), [controlledValue, uncontrolledValue]);
    // Since the contents of the text inputs can (temporarily) have out-of-range
    // values as the user is typing, we have to keep track of the text input
    // contents separately
    const [textInputValues, setTextInputValues] = useState({
        low: value.low.toString(),
        high: value.high.toString(),
    });
    // These represent the actual value of the slider as the user is dragging it around
    // It's decoupled from `value`, as we only update that when the user has
    // finished dragging
    const [sliderValues, setSliderValues] = useState(value);
    // Update internal state when the controlled value changes
    useEffect(() => {
        if (controlledValue) {
            setSliderValues(roundRanges(controlledValue));
            setTextInputValues({
                low: Math.floor(controlledValue.low).toString(),
                high: Math.ceil(controlledValue.high).toString(),
            });
        }
    }, [controlledValue]);
    const onValueChange = useDebounce((value, shouldUpdateInternalValues = false) => {
        unDebouncedOnValueChange(value);
        if (shouldUpdateInternalValues) {
            setSliderValues(value);
            setTextInputValues({
                low: Math.floor(value.low).toString(),
                high: Math.ceil(value.high).toString(),
            });
        }
    }, outputDebounceTime);
    // parses the incoming values from strings & makes sure they're within the
    // allowed range if something goes wrong during parsing, return the last
    // good value if there is an out-of-range issue, clamp the value to the
    // boundaries, respecting the `minGap` so that the `weakerValue` is adjusted
    // to accommodate the `minGap`
    const parseAndValidate = (values, weakerValue) => {
        const parsed = {
            low: Math.max(roundedBoundaryValues.low, tryParse(values.low, value.low ?? roundedBoundaryValues.low)),
            high: Math.min(roundedBoundaryValues.high, tryParse(values.high, value.high ?? roundedBoundaryValues.high)),
        };
        if (weakerValue === 'low' && parsed.low > parsed.high - minGap) {
            parsed.low = parsed.high - minGap;
        }
        else if (parsed.high < parsed.low + minGap) {
            parsed.high = parsed.low + minGap;
        }
        return parsed;
    };
    const handleKeyDown = (type) => (event) => {
        const diff = { ArrowUp: 1, ArrowDown: -1 }[event.key] ?? 0;
        if (!diff)
            return;
        setTextInputValues((prev) => {
            const next = { ...prev, [type]: (+prev[type] + diff).toString() };
            // run them through parse to clamp values
            const parsed = parseAndValidate({
                low: Math.floor(parseInt(next.low)).toString(),
                high: Math.ceil(parseInt(next.high)).toString(),
            }, type);
            setSliderValues(parsed);
            onValueChange(parsed);
            return {
                low: parsed.low.toString(),
                high: parsed.high.toString(),
            };
        });
    };
    const handleInputChange = (type) => (event) => {
        const value = event.target.value;
        setTextInputValues((prev) => {
            const next = {
                ...prev,
                [type]: value,
            };
            const parsed = parseAndValidate(next, type);
            onValueChange(parsed, true);
            setUncontrolledValue(parsed);
            return next;
        });
    };
    const handleSliderChange = (type) => (event) => 
    // there's a chance `parseValues` clamps the value to min/max, so we need
    // to update the internal state too
    setSliderValues((prev) => {
        const next = parseAndValidate({
            ...{
                low: prev.low.toString(),
                high: prev.high.toString(),
            },
            [type]: event.target.value,
        }, type);
        onValueChange(next);
        setUncontrolledValue(next);
        setTextInputValues({
            low: next.low.toString(),
            high: next.high.toString(),
        });
        return {
            low: next.low,
            high: next.high,
        };
    });
    return (jsxs("div", { "data-zta": componentTestLabel, ref: ref, className: classNames(className, style['z-range-slider']), ...otherProps, children: [jsxs("div", { className: style['z-range-slider__input-text'], children: [jsx("input", { type: "text", inputMode: "numeric", pattern: "[0-9]*", value: textInputValues.low, onKeyDown: handleKeyDown('low'), onChange: handleInputChange('low'), "aria-label": textInputLowAriaLabel || rangeInputLowAriaLabel }), jsx("span", { children: "\u2015" }), jsx("input", { type: "text", inputMode: "numeric", pattern: "[0-9]*", value: textInputValues.high, onKeyDown: handleKeyDown('high'), onChange: handleInputChange('high'), "aria-label": textInputHighAriaLabel || rangeInputHighAriaLabel }), label && jsx("span", { children: label })] }), jsxs("div", { className: style['z-range-slider__slider'], children: [jsx("span", { style: {
                            '--pp-RangeSlider-trackLeft': `${((sliderValues.low - roundedBoundaryValues.low) / (roundedBoundaryValues.high - roundedBoundaryValues.low)) * 100}%`,
                            '--pp-RangeSlider-trackRight': `${100 - ((sliderValues.high - roundedBoundaryValues.low) / (roundedBoundaryValues.high - roundedBoundaryValues.low)) * 100}%`,
                        }, className: style['z-range-slider__range-track'], id: "range_track" }), jsx("input", { type: "range", onChange: handleSliderChange('low'), min: roundedBoundaryValues.low, max: roundedBoundaryValues.high, value: sliderValues.low, "aria-label": rangeInputLowAriaLabel }), jsx("input", { type: "range", onChange: handleSliderChange('high'), min: roundedBoundaryValues.low, max: roundedBoundaryValues.high, value: sliderValues.high, "aria-label": rangeInputHighAriaLabel })] })] }));
});
RangeSlider.displayName = 'RangeSlider';

export { RangeSlider };
//# sourceMappingURL=RangeSlider.js.map
